import React from 'react';
import { useIntl } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Button } from '@vio/atlas/Button';
import OpacityAnimation from '@vio/atlas/helpers/OpacityAnimation';
import { Icon } from '@vio/atlas/Icon';
import { cssTheme } from '@vio/atlas/themes';
import { MAP_TOGGLE_BUTTON_WIDTH } from 'components/MapToggleButton/MapToggleButton';
import { SizesType } from 'types/Sizes';
import { Spacings } from 'types/Theme';
export type SwiperButtonSizeType = Exclude<SizesType, 'xs' | 'xxl' | 'lg' | 'xl'>;
const _exp = /*#__PURE__*/() => ({
  isVisible
}) => isVisible ? 'visible' : 'hidden';
const _exp2 = /*#__PURE__*/() => ({
  isVisible
}) => isVisible ? 1 : 0;
const _exp3 = /*#__PURE__*/() => ({
  shadowWidth = 120
}) => shadowWidth;
const BaseArrowGradient = /*#__PURE__*/styled('div')({
  name: "BaseArrowGradient",
  class: "bt4b1ue",
  propsAsIs: false,
  vars: {
    "bt4b1ue-0": [_exp()],
    "bt4b1ue-1": [_exp2()],
    "bt4b1ue-2": [_exp3(), "px"]
  }
});
const _exp5 = /*#__PURE__*/() => BaseArrowGradient;
const _exp8 = /*#__PURE__*/() => ({
  left = 0
}) => left;
const _exp9 = /*#__PURE__*/() => ({
  marginLeft
}) => cssTheme.layout.spacing[marginLeft];
const _exp10 = /*#__PURE__*/() => ({
  paddingLeft
}) => cssTheme.layout.spacing[paddingLeft];
const LeftArrowGradient = /*#__PURE__*/styled(_exp5())({
  name: "LeftArrowGradient",
  class: "l1orngtr",
  propsAsIs: true,
  vars: {
    "l1orngtr-0": [_exp8(), "px"],
    "l1orngtr-1": [_exp9()],
    "l1orngtr-2": [_exp10()]
  }
});
const _exp11 = /*#__PURE__*/() => BaseArrowGradient;
const _exp14 = /*#__PURE__*/() => ({
  right = MAP_TOGGLE_BUTTON_WIDTH
}) => right;
const _exp15 = /*#__PURE__*/() => ({
  marginRight
}) => cssTheme.layout.spacing[marginRight];
const _exp16 = /*#__PURE__*/() => ({
  paddingRight = 's000'
}) => cssTheme.layout.spacing[paddingRight];
const RightArrowGradient = /*#__PURE__*/styled(_exp11())({
  name: "RightArrowGradient",
  class: "r1nx4a8t",
  propsAsIs: true,
  vars: {
    "r1nx4a8t-0": [_exp14(), "px"],
    "r1nx4a8t-1": [_exp15()],
    "r1nx4a8t-2": [_exp16()]
  }
});
const _exp17 = /*#__PURE__*/() => Button;
const StyledButton = /*#__PURE__*/styled(_exp17())({
  name: "StyledButton",
  class: "s19k1vh6",
  propsAsIs: true
});
interface Props {
  /** Whether or not the button should show */
  isVisible: boolean;
  /** The direction of the arrow */
  direction: 'left' | 'right';
  /** Callback fired on button click */
  onClick: () => void;
  /** Distance from right side of wrapper */
  right?: number;
  /** Distance from left side of wrapper */
  left?: number;
  /** margin right */
  marginRight?: keyof Spacings;
  /** margin left */
  marginLeft?: keyof Spacings;
  /** padding right */
  paddingRight?: keyof Spacings;
  /** padding left */
  paddingLeft?: keyof Spacings;
  /** shadow width */
  shadowWidth?: number;
  /** DATA ID */
  dataId?: string;
  /** RTL  */
  isRtl?: boolean;
  /** Button size */
  size?: SwiperButtonSizeType;
}

/**
 * Button to handle the horizontal scrolling on the filters bar
 */
export const ScrollButton: React.FC<Props> = ({
  isVisible,
  direction,
  onClick,
  left = 0,
  right = MAP_TOGGLE_BUTTON_WIDTH,
  marginRight = 's600',
  marginLeft = 's000',
  paddingRight = 's000',
  paddingLeft = 's000',
  shadowWidth = 120,
  dataId,
  isRtl,
  size = 'sm'
}) => {
  const {
    formatMessage
  } = useIntl();
  if (direction === 'left') return <div data-chromatic="ignore" data-id={dataId}>
        <OpacityAnimation>
          <LeftArrowGradient isVisible={isVisible} left={left} shadowWidth={shadowWidth} marginLeft={marginLeft} paddingLeft={paddingLeft}>
            <StyledButton className={cx(size === 'sm' ? '__buttonSm' : '__buttonMd')} variant="secondary" size={size} isRounded isFloating onClick={onClick} ariaLabel={formatMessage({
          id: 'filters.scrollLeft',
          defaultMessage: 'Scroll left'
        })}>
              <Icon name={isRtl ? 'ChevronRight' : 'ChevronLeft'} size={size} />
            </StyledButton>
          </LeftArrowGradient>
        </OpacityAnimation>
      </div>;
  return <div data-chromatic="ignore" data-id={dataId}>
      <OpacityAnimation>
        <RightArrowGradient isVisible={isVisible} right={right} marginRight={marginRight} paddingRight={paddingRight} shadowWidth={shadowWidth}>
          <StyledButton className={cx(size === 'sm' ? '__buttonSm' : '__buttonMd')} variant="secondary" size={size} isRounded isFloating onClick={onClick} ariaLabel={formatMessage({
          id: 'filters.scrollRight',
          defaultMessage: 'Scroll right'
        })}>
            <Icon name={isRtl ? 'ChevronLeft' : 'ChevronRight'} size={size} />
          </StyledButton>
        </RightArrowGradient>
      </OpacityAnimation>
    </div>;
};

require("./ScrollButton.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ScrollButton.tsx");